import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

import tokenManager from "../../utils/tokenManager";
import { sendUserUnauthenticatedMessage } from "../../utils/postMessage";
import logger from "../../utils/logger";

export type Service = "base" | "orpheus" | "zeus" | "hermes" | "aiakos";

const ServiceUrlMap: Record<Service, string> = {
  base: import.meta.env.APP_BASE_API_URL,
  orpheus: import.meta.env.APP_BASE_ORPHEUS_URL,
  zeus: import.meta.env.APP_BASE_ZEUS_URL,
  hermes: import.meta.env.APP_BASE_HERMES_URL,
  aiakos: import.meta.env.APP_BASE_AIAKOS_URL,
};

export const getHeaders = (): Record<string, string> => {
  const token = tokenManager.getToken();

  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      "x-api-key": token,
    };
  }
  return {};
};

export const getBaseUrl = (service: Service = "base"): string => {
  return ServiceUrlMap[service];
};

export const getBaseQuery = (
  service: Service = "base",
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  const baseQuery = fetchBaseQuery({
    baseUrl: getBaseUrl(service),
    prepareHeaders: (headers, { endpoint }) => {
      const token = tokenManager.getToken();

      // Set authorization header unless fetching initial config
      if (token && endpoint !== "getConfig") {
        headers.set("authorization", `Bearer ${token}`);
        headers.set("x-api-key", token); // TODO probably not needed
      }

      return headers;
    },
  });

  // Wrapper to handle auth errors (like axios interceptors)
  // See: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#automatic-re-authorization-by-extending-fetchbasequery
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    // Handle 401/403 errors
    if (result.error) {
      const status = result.error.status;

      if (status === 401 || status === 403) {
        const url = typeof args === "string" ? args : args.url;
        logger.warn(status === 401 ? `Unauthenticated: ${url}` : `Unauthorised: ${url}`);

        // The token wipe + parent-frame notification is iframe-only behaviour. When DE components
        // run natively inside nav, window.parent === window, so the postMessage echoes back onto
        // nav's own useAuthListener and would force a re-auth on any transient 401. Nav owns auth
        // in the native context — defer to its own 401 handling instead.
        if (window !== window.parent) {
          tokenManager.removeToken();
          sendUserUnauthenticatedMessage();
        }
      }
    }

    return result;
  };
};
